import { usePaginationCDLMap } from '@centric-os/helpers';
import {
  type CDLQueryParams,
  type RecordEntityId,
  type RecordList,
  type GlobalMenuGroup,
  defaultBrandNavigationPaginationConfig,
  SortOrder,
} from '@centric-os/types';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { isEmpty } from 'lodash-es';
import { defineStore, type StateTree } from 'pinia';

interface State extends StateTree {
  globalMenuGroup: GlobalMenuGroup;
  globalMenuGroups: GlobalMenuGroup[];
}

export function useGlobalMenuGroupStore(storeId = 'globalMenuGroup') {
  const store = defineStore(storeId, {
    state: (): State => ({
      globalMenuGroup: null,
      globalMenuGroups: [],
    }),
    actions: {
      async getGlobalMenuGroups(noPagination = false, config: AxiosRequestConfig = {}) {
        const { mapCentricOSPaginationMeta, mapMenuQueryParams } = usePaginationCDLMap();

        const params: CDLQueryParams = {
          sort_by: 'name',
          sort_order: SortOrder.ASC,
          relationships: ['allowed_local_menu_groups'],
        };

        const response = await this.cdlApi.get<RecordList<GlobalMenuGroup>>(
          `/menu/v3/global-menu-groups`,
          {
            ...config,
            params: noPagination
              ? params
              : {
                  ...mapMenuQueryParams(this.pagination.queryParams),
                  relationships: params.relationships,
                },
          },
        );
        const { results, meta } = response.data;

        this.globalMenuGroups = results as GlobalMenuGroup[];

        if (!noPagination) {
          this.updatePaginationWithMeta(mapCentricOSPaginationMeta(meta));
        }
      },
      async getGlobalMenuGroup(id: RecordEntityId, params?: CDLQueryParams) {
        if (!id) return;
        const response = await this.cdlApi.get<GlobalMenuGroup>(
          `/menu/v3/global-menu-group/${id}`,
          { params },
        );
        this.globalMenuGroup = response.data;
      },
      async searchGlobalMenuGroups(
        queryParams: CDLQueryParams = {},
        config: AxiosRequestConfig = {},
      ) {
        const params: CDLQueryParams = {
          sort_by: 'name',
          sort_order: SortOrder.ASC,
        };
        const response = await this.cdlApi.get<RecordList<GlobalMenuGroup>>(
          `/menu/v3/global-menu-groups`,
          {
            ...config,
            params: !isEmpty(queryParams) ? queryParams : params,
          },
        );
        const { results } = response.data;
        this.globalMenuGroups = results as GlobalMenuGroup[];
      },
      async createGlobalMenuGroup(payload: Partial<GlobalMenuGroup>): Promise<GlobalMenuGroup> {
        const newPayload = JSON.parse(JSON.stringify(payload));
        newPayload.posid_segment = String(newPayload.posid_segment);
        const response = await this.cdlApi.post<
          GlobalMenuGroup,
          AxiosResponse<GlobalMenuGroup, any>,
          Partial<GlobalMenuGroup>
        >('/menu/v3/global-menu-group', newPayload);
        return response.data;
      },
      async updateGlobalMenuGroup(payload: Partial<GlobalMenuGroup>): Promise<GlobalMenuGroup> {
        if (payload?.id) {
          const { version, posid_segment, ...cleanedUpPayload } = payload;

          const response = await this.cdlApi.patch<
            GlobalMenuGroup,
            AxiosResponse<GlobalMenuGroup, any>,
            Partial<GlobalMenuGroup>
          >(`/menu/v3/global-menu-group/${payload.id}`, cleanedUpPayload);
          return response.data;
        } else {
          return Promise.reject(new Error('no global menu group selected'));
        }
      },
      async deleteGlobalMenuGroup(id: RecordEntityId) {
        await this.cdlApi.delete<
          GlobalMenuGroup,
          AxiosResponse<GlobalMenuGroup, any>,
          Partial<GlobalMenuGroup>
        >(`/menu/v3/global-menu-group/${id}?delete_posid=true`);
      },
    },
    pagination: true,
    paginationConfig: {
      ...defaultBrandNavigationPaginationConfig,
    },
  });
  return store();
}
